/**
 * Shared domain + API contract types for Vigil (uptime-mon).
 * Imported by BOTH the Bun server (src/server) and the React client (src/web).
 *
 * Conventions:
 * - All timestamps are unix epoch MILLISECONDS (number).
 * - DB rows are snake_case; everything crossing the API boundary is camelCase
 *   (mapping happens in src/server/repo.ts).
 */

// ---------------------------------------------------------------------------
// Monitors
// ---------------------------------------------------------------------------

export type MonitorType = "http" | "keyword" | "ping" | "tcp" | "dns" | "push";

/** Heartbeat status values (stored in heartbeats.status). */
export const STATUS = {
  DOWN: 0,
  UP: 1,
  PENDING: 2,
  MAINTENANCE: 3,
} as const;
export type StatusValue = (typeof STATUS)[keyof typeof STATUS];

export interface Monitor {
  id: number;
  name: string;
  type: MonitorType;
  /** http/keyword only */
  url: string | null;
  /** ping/tcp/dns only */
  hostname: string | null;
  /** tcp only */
  port: number | null;
  intervalSeconds: number;
  timeoutSeconds: number;
  /** failed checks tolerated (as PENDING) before the monitor turns DOWN */
  retries: number;
  retryIntervalSeconds: number;
  httpMethod: string;
  /** entries are single codes ("301") or ranges ("200-299") */
  acceptedStatusCodes: string[];
  requestHeaders: Record<string, string> | null;
  requestBody: string | null;
  followRedirects: boolean;
  ignoreTls: boolean;
  /** keyword type only */
  keyword: string | null;
  /** false = keyword must be present, true = must be absent */
  keywordInvert: boolean;
  /** dns type only: A | AAAA | CNAME | MX | TXT | NS */
  dnsRecordType: string;
  dnsResolver: string | null;
  /** substring expected in the resolved records (optional) */
  dnsExpected: string | null;
  /** push type only — secret path segment of the push URL */
  pushToken: string | null;
  groupId: number | null;
  active: boolean;
  /** days until TLS cert expiry, refreshed periodically for https monitors */
  certExpiryDays: number | null;
  certCheckedAt: number | null;
  createdAt: number;
}

/** Create/update payload. Server validates per type and applies defaults. */
export interface MonitorInput {
  name: string;
  type: MonitorType;
  url?: string | null;
  hostname?: string | null;
  port?: number | null;
  intervalSeconds?: number;
  timeoutSeconds?: number;
  retries?: number;
  retryIntervalSeconds?: number;
  httpMethod?: string;
  acceptedStatusCodes?: string[];
  requestHeaders?: Record<string, string> | null;
  requestBody?: string | null;
  followRedirects?: boolean;
  ignoreTls?: boolean;
  keyword?: string | null;
  keywordInvert?: boolean;
  dnsRecordType?: string;
  dnsResolver?: string | null;
  dnsExpected?: string | null;
  groupId?: number | null;
  /** notification channel ids attached to this monitor */
  notificationIds?: number[];
}

export interface Heartbeat {
  id: number;
  monitorId: number;
  status: StatusValue;
  latencyMs: number | null;
  message: string | null;
  /** true when this beat is an UP<->DOWN transition (shown in event logs) */
  important: boolean;
  checkedAt: number;
}

/** GET /api/monitors list item. */
export interface MonitorListItem extends Monitor {
  /** last ~30 up/down/pending/maintenance beats, OLDEST FIRST */
  lastBeats: Heartbeat[];
  /** effective status: MAINTENANCE when inside an active window, else last beat status; null = never checked */
  currentStatus: StatusValue | null;
  uptime24h: number | null;
  lastLatency: number | null;
  lastCheckedAt: number | null;
}

/** GET /api/monitors/:id */
export interface MonitorDetail extends Monitor {
  notificationIds: number[];
}

export interface MonitorGroup {
  id: number;
  name: string;
  sortOrder: number;
  monitorCount: number;
}

export type StatsPeriod = "24h" | "7d" | "30d";

export interface StatsPoint {
  /** bucket start (ms) */
  t: number;
  avgLatency: number | null;
  up: number;
  down: number;
}

/** GET /api/monitors/:id/stats?period= */
export interface MonitorStats {
  period: StatsPeriod;
  /** 0..1 ratio, null when no up/down beats in period */
  uptime: number | null;
  avgLatency: number | null;
  minLatency: number | null;
  maxLatency: number | null;
  points: StatsPoint[];
}

/** GET /api/events (global) and /api/monitors/:id/events — important beats, newest first */
export interface EventItem extends Heartbeat {
  monitorName: string;
  monitorType: MonitorType;
}

// ---------------------------------------------------------------------------
// Notification channels
// ---------------------------------------------------------------------------

export type NotificationType =
  | "webhook"
  | "slack"
  | "discord"
  | "telegram"
  | "ntfy"
  | "smtp";

/** Per-type config payload stored as JSON. The web forms and the server
 * providers MUST both follow these exact shapes. */
export interface NotificationConfigMap {
  webhook: { url: string; headers?: Record<string, string> };
  slack: { webhookUrl: string };
  discord: { webhookUrl: string };
  telegram: { botToken: string; chatId: string };
  ntfy: { serverUrl?: string; topic: string; priority?: number; token?: string };
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    username?: string;
    password?: string;
    from: string;
    to: string;
  };
}

export interface NotificationChannel {
  id: number;
  name: string;
  type: NotificationType;
  config: Record<string, unknown>;
  /** pre-selected by the UI when creating new monitors */
  isDefault: boolean;
  createdAt: number;
}

export interface NotificationInput {
  name: string;
  type: NotificationType;
  config: Record<string, unknown>;
  isDefault?: boolean;
}

/** Payload handed to providers (and POSTed by the generic webhook provider). */
export interface NotifyPayload {
  status: "up" | "down" | "test";
  monitor?: { id: number; name: string; type: MonitorType };
  message: string;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Status pages
// ---------------------------------------------------------------------------

export interface StatusPage {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  /** emoji or image URL rendered before the title */
  icon: string | null;
  published: boolean;
  customDomain: string | null;
  showPoweredBy: boolean;
  hasPassword: boolean;
  createdAt: number;
}

export interface StatusPageGroupConfig {
  id: number;
  name: string;
  sortOrder: number;
  monitorIds: number[];
}

export interface StatusPageDetail extends StatusPage {
  groups: StatusPageGroupConfig[];
  incidents: Incident[];
}

/** POST /api/status-pages and PUT /api/status-pages/:id.
 * - `password`: string sets a new password, null clears it, undefined keeps current.
 * - `groups` fully replaces the page's group/monitor layout when provided. */
export interface StatusPageInput {
  slug: string;
  title: string;
  description?: string | null;
  icon?: string | null;
  published?: boolean;
  customDomain?: string | null;
  showPoweredBy?: boolean;
  password?: string | null;
  groups?: { name: string; monitorIds: number[] }[];
}

export type IncidentStyle = "info" | "warning" | "danger" | "success";

export interface Incident {
  id: number;
  statusPageId: number;
  title: string;
  content: string;
  style: IncidentStyle;
  pinned: boolean;
  createdAt: number;
  resolvedAt: number | null;
}

export interface IncidentInput {
  title: string;
  content: string;
  style?: IncidentStyle;
}

// ---------------------------------------------------------------------------
// Public status page payload (unauthenticated — NEVER leak urls/hostnames)
// ---------------------------------------------------------------------------

export type OverallStatus = "up" | "degraded" | "down" | "maintenance" | "unknown";

export interface DailyUptime {
  /** YYYY-MM-DD (UTC) */
  day: string;
  /** 0..1 ratio, null = no data that day */
  uptime: number | null;
  down: number;
}

export interface PublicMonitor {
  id: number;
  name: string;
  status: StatusValue | null;
  uptime24h: number | null;
  uptime30d: number | null;
  uptime90d: number | null;
  avgLatency24h: number | null;
  /** last 90 days including today, OLDEST FIRST */
  daily: DailyUptime[];
}

export interface PublicStatusGroup {
  id: number;
  name: string;
  monitors: PublicMonitor[];
}

/** GET /api/status/:slug — 200 with this payload, or 401 {needsPassword:true} */
export interface PublicStatusPage {
  page: {
    slug: string;
    title: string;
    description: string | null;
    icon: string | null;
    showPoweredBy: boolean;
  };
  overallStatus: OverallStatus;
  /** unresolved incidents (pinned first, newest first) plus resolved ones from the last 90 days */
  incidents: Incident[];
  groups: PublicStatusGroup[];
  generatedAt: number;
}

// ---------------------------------------------------------------------------
// Maintenance windows
// ---------------------------------------------------------------------------

export interface MaintenanceWindow {
  id: number;
  title: string;
  startAt: number;
  endAt: number;
  monitorIds: number[];
  createdAt: number;
}

export interface MaintenanceInput {
  title: string;
  startAt: number;
  endAt: number;
  monitorIds: number[];
}

// ---------------------------------------------------------------------------
// Auth + settings
// ---------------------------------------------------------------------------

export interface AuthStatus {
  needsSetup: boolean;
  authenticated: boolean;
  username: string | null;
}

export interface AppSettings {
  /** raw heartbeat retention (stats_daily is kept forever) */
  retentionDays: number;
  /** bearer key for the /mcp endpoint; null = MCP disabled */
  mcpApiKey: string | null;
}

// ---------------------------------------------------------------------------
// API error envelope: every non-2xx JSON response is { error: string }
// ---------------------------------------------------------------------------
export interface ApiErrorBody {
  error: string;
  needsPassword?: boolean;
}
